import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api";
import CheckoutModal from "../components/CheckoutModal";
import { applyMeta } from "../lib/meta";
import { Loader2, ArrowLeft, CalendarClock, ShoppingCart } from "lucide-react";
import { PromoBanner } from "../components/PromoBanner";

export default function ProductPage() {
  const { slug, productSlug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [buy, setBuy] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/store/${slug}/vouchers/${productSlug}`)
      .then(({ data }) => {
        setData(data);
        const p = data.product, b = data.business;
        applyMeta({
          title: p.seo_title || `${p.title} — ${b.name}`,
          description: p.seo_description || p.description || `Buy a ${p.title} gift voucher from ${b.name}.`,
          image: p.share_image || p.image_url || b.hero_image || b.logo_url,
        });
      })
      .catch((e) => setError(e?.response?.status === 423 ? "This store is temporarily unavailable. Please check back soon." : "Voucher not found"));
  }, [slug, productSlug]);

  if (error) return <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-[#5a6b80]">{error}<Link to={`/store/${slug}`} className="gp-btn-teal rounded-full px-6 py-2.5 font-medium">Back to store</Link></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={40} /></div>;

  const b = data.business;
  const p = data.product;
  const brand = b.brand_color || "#16243b";
  const textColor = b.text_color || "#16243b";
  const btnBg = b.button_color || "#2ec9c9";
  const btnText = b.button_text_color || "#ffffff";
  const images = [p.image_url, ...((p.gallery_images) || [])].filter(Boolean);
  const main = images[activeImg] || images[0];

  return (
    <div className="min-h-screen bg-[#f6f8fb]">
      <PromoBanner promo={b.promo_banner} />
      <div className="py-5" style={{ background: brand }}>
        <div className="max-w-[1000px] mx-auto px-6 flex items-center justify-between">
          <Link to={`/store/${b.slug}`} className="text-white/90 hover:text-white flex items-center gap-1.5 text-sm font-medium"><ArrowLeft size={16} /> {b.name}</Link>
          {b.logo_url && <img src={b.logo_url} alt={b.name} className="h-14 object-contain" />}
        </div>
      </div>

      <div className="max-w-[1000px] mx-auto px-6 py-12 grid lg:grid-cols-2 gap-12">
        {/* Gallery */}
        <div>
          <div className="rounded-2xl overflow-hidden bg-white shadow-[0_15px_40px_rgba(20,30,55,0.1)] aspect-square">
            {main ? <img src={main} alt={p.title} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center" style={{ background: brand }}><ShoppingCart className="text-white/70" size={48} /></div>}
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {images.map((src, i) => (
                <button key={i} onClick={() => setActiveImg(i)}
                  className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors ${i === activeImg ? "border-[#2ec9c9]" : "border-transparent"}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <h1 className="text-[2.2rem] font-bold leading-tight" style={{ color: textColor }}>{p.title}</h1>
          <p className="text-[2rem] font-bold mt-3" style={{ color: brand }}>£{p.price.toFixed(2)}</p>
          {p.kind === "experience" && (
            <div className="mt-3 bg-[#eefafa] rounded-xl p-4" data-testid="product-experience-info">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#1fb0b0]">Experience voucher · used once</p>
              {p.experience_details && <p className="text-sm text-[#16243b] mt-1 whitespace-pre-line">{p.experience_details}</p>}
            </div>
          )}
          {p.description && <p className="text-[#5a6b80] text-[1.05rem] mt-4">{p.description}</p>}
          {p.long_description && <p className="text-[#5a6b80] leading-relaxed mt-4 whitespace-pre-line">{p.long_description}</p>}

          <button onClick={() => setBuy(true)} data-testid="buy-voucher-btn" className="rounded-full px-8 py-3.5 text-[1.05rem] font-medium mt-7 transition-transform hover:-translate-y-0.5" style={{ background: btnBg, color: btnText }}>Buy this voucher</button>

          {b.voucher_validity_months != null && (
            <p className="text-sm text-[#8a97a8] mt-4 flex items-center gap-1.5"><CalendarClock size={15} /> Valid for {b.voucher_validity_months} months from purchase</p>
          )}

          {p.terms && (
            <div className="mt-8 border-t border-gray-200 pt-6">
              <h3 className="font-semibold mb-2" style={{ color: textColor }}>Terms &amp; conditions</h3>
              <p className="text-sm text-[#5a6b80] leading-relaxed whitespace-pre-line">{p.terms}</p>
            </div>
          )}
        </div>
      </div>

      <footer className="text-center py-8 text-[#8a97a8] text-sm">Powered by GiftVouchers.app</footer>

      {buy && <CheckoutModal slug={b.slug} product={p} promo={b.promo_banner} videoEnabled={b.video_messages_enabled !== false} scheduleEnabled={(data.features || []).includes("scheduled_delivery")} smsEnabled={(data.features || []).includes("sms_delivery")} onClose={() => setBuy(false)} />}
    </div>
  );
}
